<template>
  <!-- First Priority is displaying an error state  -->
  <slot
    v-if="props.response.error.value"
    :message="props.response.error.value.message"
    name="error"
  >
    <TCalloutExclamationBlock>
      {{ props.response.error.value.message ?? $t('generic_error') }}
    </TCalloutExclamationBlock>
  </slot>

  <!-- Second Priority is displaying a loading state -->
  <slot v-else-if="props.response.fetching.value" name="fetching">
    <TSkeleton size="small" />
    <TSkeleton size="small" />
    <TSkeleton size="small" />
    <TSkeleton size="small" />
    <TSkeleton size="small" />
  </slot>

  <!-- Third Priority is displaying an empty state if the data is empty -->
  <slot v-else-if="isDataEmpty" name="emptyState">
    <div v-if="!ignoreEmptyState" class="text-bold text-2xl">
      {{ $t('generic_empty_state') }}
    </div>
  </slot>

  <!-- Lastly, display data -->

  <slot
    v-else-if="props.response.data.value"
    :data="props.response.data.value"
  ></slot>
</template>

<script lang="ts" setup generic="T">
import { type InfrastructureResponse } from '@teachino/shared-infrastructure';
import TCalloutExclamationBlock from './Callout/TCalloutExclamationBlock.vue';
import TSkeleton from './TSkeleton.vue';
import { $t } from '@teachino/shared-i18n';
import { computed } from 'vue';
import type { UseQueryResponse } from '@urql/vue';

const props = withDefaults(
  defineProps<{
    response: InfrastructureResponse<T> | UseQueryResponse<T>;
    ignoreEmptyState?: boolean;
  }>(),
  {
    ignoreEmptyState: false,
  },
);

const isDataEmpty = computed(() => {
  const value = props.response.data.value;

  const isNullish = !value;
  const isEmptyArray = Array.isArray(value) && value.length === 0;
  const hasOnlyEmptyValues =
    typeof value === 'object' && value !== null
      ? Object.values(value as Record<string, unknown>).every(
          (v) => Array.isArray(v) && v.length === 0,
        )
      : false;
  return isNullish || isEmptyArray || hasOnlyEmptyValues;
});
</script>
